import Auth from '@js/plugins/auth';

export const UPLOAD_NOTSTARTED = 'notstarted';
export const UPLOAD_PROCESSING = 'processing';
export const UPLOAD_UPLOADED = 'uploaded';
export const UPLOAD_VALIDATING = 'validating';
export const UPLOAD_VALIDATION_FAILED = 'validation_failed';
export const UPLOAD_STORING = 'storing';
export const UPLOAD_COMPLETED = 'completed';

class ImportData {
    constructor() {
        this.errors = [];
        this.totalErrors = 0;
        this.totalProcessed = {
            total: 0,
            import_key: null,
        };
        this.totalUploaded = 0;
        this.totalValidated = 0;
        this.totalStored = 0;
        this.totalRows = 0;
        this.uploadProgress = UPLOAD_NOTSTARTED;
        this.uploadingPercentage = 0;
        this.importKey = null;
        this.rows = [];
        this.headings = [];
        this.mapper = [];
        this.pollInterval = null;
        this.pollingTimeInMilliseconds = 10000;
        this.eventCallback = {
            fileUploaded: () => {},
            uploadCompleted: () => {},
            itemPassValidation: () => {},
            itemFailValidation: () => {},
            serverError: () => {},
            pollingRequested: () => {},
        };
    }

    initEchoIfNotInitialize() {
        if (window.Echo === undefined) {
            require('../bootstrap/echo');
        }

        this.laravelEcho = window.Echo;
        this.listenToImportEvent();
    }

    setUploadProgress(progress) {
        this.uploadProgress = progress;
    }

    setImportKey(importKey) {
        this.importKey = importKey;
    }

    setTotalRows(totalRows) {
        this.totalRows = totalRows;
    }

    setErrors(errors) {
        this.errors = errors;
    }

    setMapper(mapper) {
        this.mapper = mapper;
    }

    hasErrors() {
        return this.errors.length;
    }

    setRowsAndHeadings(data) {
        this.rows = data.rows;
        this.headings = data.headings;
    }

    getUploadingPercentage() {
        return Math.trunc((this.totalUploaded / this.totalRows) * 100);
    }

    getValidatingPercentage() {
        return Math.trunc((this.totalValidated / this.totalRows) * 100);
    }

    getStoringPercentage() {
        return Math.trunc((this.totalStored / this.totalRows) * 100);
    }

    addTotalProcessed(total_processed, import_key) {
        this.totalProcessed.total += total_processed;

        if (!this.totalProcessed.import_key) {
            this.totalProcessed.import_key = import_key;
        }
    }

    addTotalUploaded(total_uploaded) {
        this.totalUploaded += total_uploaded;
    }

    addTotalValidated(total_validated) {
        this.totalValidated += total_validated;
    }

    addTotalStored(total_stored) {
        this.totalStored += total_stored;
    }

    syncProcessedWithUploaded() {
        if (this.totalProcessed.import_key == this.importKey) {
            this.totalUploaded = this.totalProcessed.total;
        }
    }

    onItemPassValidation(callback) {
        this.eventCallback.itemPassValidation = callback;
    }

    onItemFailValidation(callback) {
        this.eventCallback.itemFailValidation = callback;
    }

    onImportCompleted(callback) {
        this.eventCallback.uploadCompleted = callback;
    }

    onFileUploaded(callback) {
        this.eventCallback.fileUploaded = callback;
    }

    onServerError(callback) {
        this.eventCallback.serverError = callback;
    }

    onPollingRequested(callback) {
        this.eventCallback.pollingRequested = callback;
    }

    reset() {
        this.errors = [];
        this.totalErrors = 0;
        this.totalProcessed = {
            total: 0,
            import_key: null,
        };
        this.totalUploaded = 0;
        this.totalValidated = 0;
        this.totalStored = 0;
        this.totalRows = 0;
        this.uploadProgress = UPLOAD_NOTSTARTED;
        this.uploadingPercentage = 0;
        this.importKey = null;
        this.rows = [];
        this.headings = [];
        this.mapper = [];
    }

    listenToImportEvent() {
        this.laravelEcho
            .private(`user.${Auth.user().id}`)
            .listen('.import.chunk-processed', e => {
                // In case event is broadcasted before importKey is received
                this.addTotalProcessed(e.total_processed, e.import_key);

                this.delayPolling();

                if (e.import_key == this.importKey) {
                    this.addTotalUploaded(e.total_processed);
                    this.syncProcessedWithUploaded();

                    if (this.totalUploaded == this.totalRows) {
                        this.setUploadProgress(UPLOAD_UPLOADED);
                        this.eventCallback.fileUploaded();
                    }
                }
            })
            .listen('.import.failed-job', e => {
                if (e.import_key == this.importKey) {
                    this.eventCallback.serverError(e.exception);
                }
            })
            .listen('.import.validate-result', e => {
                if (e.import_key == this.importKey) {
                    this.addTotalValidated(e.total_validated);

                    this.delayPolling();

                    // only shows max 50 errors
                    if (!_.isEmpty(e.errors)) {
                        Object.keys(e.errors).forEach((k, i) => {
                            Array.from(e.errors[k]).forEach(v => {
                                if (this.errors.length < 50) {
                                    this.errors.push(v);
                                }

                                this.totalErrors++;
                            });
                        });
                    }

                    if (this.totalValidated == this.totalRows) {
                        if (this.errors.length) {
                            this.setUploadProgress(UPLOAD_VALIDATION_FAILED);
                            this.eventCallback.itemFailValidation();
                        } else {
                            this.setUploadProgress(UPLOAD_STORING);
                            this.eventCallback.itemPassValidation();
                        }
                    }
                }
            })
            .listen('.import.stored-data', e => {
                if (e.import_key == this.importKey) {
                    this.addTotalStored(e.total_stored);

                    this.delayPolling();

                    if (this.totalStored == this.totalRows) {
                        this.setUploadProgress(UPLOAD_COMPLETED);
                        this.eventCallback.uploadCompleted();
                    }
                }
            });
    }

    startPolling() {
        this.pollInterval = setTimeout(
            this.pollImportStatus.bind(this),
            this.pollingTimeInMilliseconds,
        );
    }

    pollImportStatus() {
        this.startPolling();
        this.eventCallback.pollingRequested();
    }

    delayPolling() {
        clearTimeout(this.pollInterval);
        this.startPolling();
    }

    stopPolling() {
        clearTimeout(this.pollInterval);
    }

    syncImportStatus(importStatus) {
        if (this.hasFailedImportStatus(importStatus)) {
            this.eventCallback.serverError(
                this.getFailedStageMessage(importStatus),
            );
        }

        if (this.uploadProgress == UPLOAD_PROCESSING) {
            this.syncUploadProgress(importStatus);
        }

        if (this.uploadProgress == UPLOAD_VALIDATING) {
            this.syncValidateProgress(importStatus);
        }

        if (this.uploadProgress == UPLOAD_STORING) {
            this.syncStorageProgress(importStatus);
        }
    }

    hasFailedImportStatus(importStatus) {
        const importStatuses = [
            importStatus['process-status'],
            importStatus['validate-status'],
            importStatus['store-status'],
        ];

        return importStatuses.some(status => status == 'failed');
    }

    getFailedStageMessage(importStatus) {
        if (importStatus['process-status'] === 'failed') {
            return 'Failed while processing the uploaded file.';
        }
        if (importStatus['validate-status'] === 'failed') {
            return 'Failed while validating import data.';
        }
        if (importStatus['store-status'] === 'failed') {
            return 'Failed while saving import data.';
        }
        return null;
    }

    syncUploadProgress(importStatus) {
        this.totalUploaded = importStatus['process-rows'];

        if (importStatus['process-status'] == 'completed') {
            this.setUploadProgress(UPLOAD_UPLOADED);
            this.eventCallback.fileUploaded();
        }
    }

    syncValidateProgress(importStatus) {
        this.totalValidated = importStatus['validate-rows'];

        if (importStatus['validate-status'] == 'completed') {
            this.setUploadProgress(UPLOAD_STORING);
            this.eventCallback.itemPassValidation();
        }
    }

    syncStorageProgress(importStatus) {
        this.totalStored = importStatus['store-rows'];

        if (importStatus['store-status'] == 'completed') {
            this.setUploadProgress(UPLOAD_COMPLETED);
            this.eventCallback.uploadCompleted();
        }
    }
}

export default new ImportData();



// WEBPACK FOOTER //
// ./resources/assets/js/plugins/import-data.js